// Pure enemy simulation for the 2D platformer. Every behaviour is a small
// timed state machine over `EnemyRuntime`; nothing here reads the DOM,
// three.js or `Math.random`, so a level's enemies play out identically on
// every machine and can be unit-tested frame by frame.
import type { EnemyDef, EnemyRuntime, EnemyState, Projectile, ProjectileSpec } from './combat2d';
import { boxesOverlap } from './combat2d';
import type { CrumbleTileState, PlatformerEvent, PlatformerGrid } from './platformer2d';
import { boxCollidesSolid, isSolidAt } from './platformer2d';

export interface EnemyPlayerView {
  x: number;
  y: number;
  hw: number;
  hh: number;
  /** Shadow Form: enemies neither see nor hurt the player. */
  isPhasing: boolean;
}

export interface EnemyStepResult {
  enemies: Record<string, EnemyRuntime>;
  events: PlatformerEvent[];
  /** Enemy projectiles fired this step, ready to append to the state. */
  projectiles: Projectile[];
  /** Total contact damage the player takes this step (0 or more). Enemy
   * ids that caused it are in `contactSources`, nearest first. */
  contactDamage: number;
  contactSources: { id: string; x: number }[];
}

/** Gravity and terminal speed enemies fall with; matches the player's feel. */
export const ENEMY_GRAVITY = 30;
export const ENEMY_MAX_FALL = 18;
/** Seconds a hurt flash lasts and how long the death clip is held. */
export const ENEMY_HURT_FLASH_SECONDS = 0.12;
export const ENEMY_DEATH_HOLD_SECONDS = 0.6;
/** Seconds a charger or boss rests after a charge hits a wall or ledge. */
const CHARGE_RECOVER_SECONDS = 0.6;
/** Seconds a shield enemy waits between re-facing the player. */
const SHIELD_TURN_SECONDS = 1.2;
/** Seconds a flyer's dive lasts before it returns to its patrol height. */
const FLYER_DIVE_SECONDS = 0.55;
const FLYER_RETURN_SECONDS = 0.7;

let projectileCounter = 0;
/** Reset the projectile id counter (tests). */
export function resetEnemyProjectileIds(): void {
  projectileCounter = 0;
}

function makeProjectile(
  owner: 'player' | 'enemy',
  spec: ProjectileSpec,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
): Projectile {
  const len = Math.hypot(dirX, dirY) || 1;
  projectileCounter += 1;
  return {
    id: `${owner}-p${projectileCounter}`,
    x,
    y,
    vx: (dirX / len) * spec.speed,
    vy: (dirY / len) * spec.speed,
    owner,
    damage: spec.damage,
    radius: spec.radius,
    travelled: 0,
    spec,
    returning: false,
    hitIds: [],
    originX: x,
    originY: y,
  };
}

export { makeProjectile };

/** True when there is solid ground under the given foot position. */
function groundAhead(grid: PlatformerGrid, footX: number, feetY: number, crumble?: Record<number, CrumbleTileState>): boolean {
  return isSolidAt(grid, footX, feetY + 0.05, false, crumble) || isSolidAt(grid, footX, feetY + 0.5, false, crumble);
}

/**
 * Move an enemy along one axis with AABB tile collision, returning the
 * resolved position and whether it was blocked. Flying enemies skip tiles.
 */
function moveAxis(
  grid: PlatformerGrid,
  e: EnemyRuntime,
  def: EnemyDef,
  dx: number,
  dy: number,
  crumble?: Record<number, CrumbleTileState>,
): { x: number; y: number; blockedX: boolean; blockedY: boolean } {
  let x = e.x;
  let y = e.y;
  let blockedX = false;
  let blockedY = false;
  if (def.flying) return { x: x + dx, y: y + dy, blockedX, blockedY };
  if (dx !== 0) {
    const nx = x + dx;
    if (!boxCollidesSolid(grid, nx, y, def.halfWidth, def.halfHeight, false, crumble)) x = nx;
    else blockedX = true;
  }
  if (dy !== 0) {
    const ny = y + dy;
    if (!boxCollidesSolid(grid, x, ny, def.halfWidth, def.halfHeight, false, crumble)) {
      y = ny;
    } else {
      blockedY = true;
      if (dy > 0) {
        const tileY = Math.floor(ny + def.halfHeight);
        y = tileY - def.halfHeight - 0.001;
      } else {
        const tileY = Math.floor(ny - def.halfHeight);
        y = tileY + 1 + def.halfHeight + 0.001;
      }
    }
  }
  return { x, y, blockedX, blockedY };
}

function canHurtByContact(e: EnemyRuntime): boolean {
  const s: EnemyState = e.state;
  return s !== 'dead' && s !== 'stunned' && s !== 'hurt';
}

function faceToward(e: EnemyRuntime, targetX: number): 'left' | 'right' {
  return targetX < e.x ? 'left' : 'right';
}

function within(e: EnemyRuntime, p: EnemyPlayerView, range: number): boolean {
  return range > 0 && Math.hypot(p.x - e.x, p.y - e.y) <= range;
}

/**
 * Step every enemy by `dt`. `defs` maps enemy id to its definition. The
 * player view is read-only; damage to the player is reported, not applied,
 * so `stepPlatformer` can route it through its own invulnerability rules.
 */
export function stepEnemies(
  enemies: Record<string, EnemyRuntime>,
  defs: Record<string, EnemyDef>,
  grid: PlatformerGrid,
  player: EnemyPlayerView,
  dt: number,
  crumble?: Record<number, CrumbleTileState>,
): EnemyStepResult {
  const out: Record<string, EnemyRuntime> = {};
  const events: PlatformerEvent[] = [];
  const projectiles: Projectile[] = [];
  let contactDamage = 0;
  const contactSources: { id: string; x: number }[] = [];

  for (const id of Object.keys(enemies)) {
    const def = defs[id];
    const prev = enemies[id]!;
    if (!def) {
      out[id] = prev;
      continue;
    }
    const e: EnemyRuntime = { ...prev, hurtFlash: Math.max(0, prev.hurtFlash - dt), clock: prev.clock + dt };

    if (e.state === 'dead') {
      e.deathTimer += dt;
      out[id] = e;
      continue;
    }
    if (e.hp <= 0) {
      e.state = 'dead';
      e.vx = 0;
      e.vy = 0;
      e.deathTimer = 0;
      events.push({ type: 'enemy-death', enemyId: id, x: e.x, y: e.y, tag: def.tag });
      out[id] = e;
      continue;
    }

    e.cooldownTimer = Math.max(0, e.cooldownTimer - dt);

    if (e.state === 'stunned' || e.state === 'hurt') {
      e.stateTimer -= dt;
      // Knockback decays while stunned; gravity still applies to walkers.
      e.vx *= Math.max(0, 1 - dt * 8);
      if (!def.flying) e.vy = Math.min(ENEMY_MAX_FALL, e.vy + ENEMY_GRAVITY * dt);
      const m = moveAxis(grid, e, def, e.vx * dt, e.vy * dt, crumble);
      e.x = m.x;
      e.y = m.y;
      if (m.blockedX) e.vx = 0;
      if (m.blockedY) {
        e.isGrounded = e.vy > 0;
        e.vy = 0;
      }
      if (e.stateTimer <= 0) {
        e.state = 'idle';
        e.stateTimer = 0;
      }
      out[id] = e;
      continue;
    }

    switch (def.behaviour) {
      case 'patrol':
        stepPatrol(e, def, grid, dt, crumble);
        break;
      case 'shield':
        stepShield(e, def, grid, player, dt, crumble);
        break;
      case 'hop':
        stepHop(e, def, grid, player, dt, events, crumble);
        break;
      case 'flyer':
        stepFlyer(e, def, player, dt, events);
        break;
      case 'turret':
        stepTurret(e, def, player, dt, events, projectiles);
        break;
      case 'charger':
        stepCharger(e, def, grid, player, dt, events, crumble);
        break;
      case 'boss':
        stepBoss(e, def, grid, player, dt, events, projectiles, crumble);
        break;
    }

    // Contact damage.
    if (!player.isPhasing && canHurtByContact(e)) {
      if (boxesOverlap(e.x, e.y, def.halfWidth, def.halfHeight, player.x, player.y, player.hw, player.hh)) {
        contactDamage += def.contactDamage;
        contactSources.push({ id, x: e.x });
      }
    }
    out[id] = e;
  }

  contactSources.sort((a, b) => Math.abs(a.x - player.x) - Math.abs(b.x - player.x));
  return { enemies: out, events, projectiles, contactDamage, contactSources };
}

/** Gravity plus grounded bookkeeping for a walking enemy. */
function applyWalkerGravity(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  dt: number,
  crumble?: Record<number, CrumbleTileState>,
): void {
  if (def.flying) return;
  e.vy = Math.min(ENEMY_MAX_FALL, e.vy + ENEMY_GRAVITY * dt);
  const m = moveAxis(grid, e, def, 0, e.vy * dt, crumble);
  e.y = m.y;
  if (m.blockedY) {
    e.isGrounded = e.vy > 0;
    e.vy = 0;
  } else {
    e.isGrounded = false;
  }
}

/** Walk sideways at `speed` in the facing direction, turning at walls,
 * ledges and the patrol bounds. Returns true when the enemy turned. */
function walk(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  speed: number,
  dt: number,
  crumble?: Record<number, CrumbleTileState>,
  turnAtLedges = true,
): boolean {
  const dir = e.facing === 'right' ? 1 : -1;
  const dx = dir * speed * dt;
  const m = moveAxis(grid, e, def, dx, 0, crumble);
  let turned = false;
  if (m.blockedX) {
    turned = true;
  } else {
    e.x = m.x;
    const frontX = e.x + dir * (def.halfWidth + 0.05);
    const feetY = e.y + def.halfHeight;
    if (turnAtLedges && e.isGrounded && !groundAhead(grid, frontX, feetY, crumble)) turned = true;
    if (def.minX !== undefined && e.x < def.minX) turned = true;
    if (def.maxX !== undefined && e.x > def.maxX) turned = true;
  }
  if (turned) {
    e.facing = e.facing === 'right' ? 'left' : 'right';
    if (def.minX !== undefined) e.x = Math.max(def.minX, e.x);
    if (def.maxX !== undefined) e.x = Math.min(def.maxX, e.x);
  }
  e.vx = turned ? 0 : dir * speed;
  return turned;
}

function stepPatrol(e: EnemyRuntime, def: EnemyDef, grid: PlatformerGrid, dt: number, crumble?: Record<number, CrumbleTileState>): void {
  applyWalkerGravity(e, def, grid, dt, crumble);
  e.state = 'move';
  walk(e, def, grid, def.speed, dt, crumble);
}

function stepShield(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  player: EnemyPlayerView,
  dt: number,
  crumble?: Record<number, CrumbleTileState>,
): void {
  applyWalkerGravity(e, def, grid, dt, crumble);
  const sees = within(e, player, def.aggroRange) && !player.isPhasing;
  if (sees) {
    // Turn toward the player only every SHIELD_TURN_SECONDS so there is a
    // window to get behind it.
    if (e.cooldownTimer <= 0) {
      e.facing = faceToward(e, player.x);
      e.cooldownTimer = SHIELD_TURN_SECONDS;
    }
    e.state = 'move';
    const dir = e.facing === 'right' ? 1 : -1;
    const toward = Math.sign(player.x - e.x);
    // Walk toward the player only while facing them; never off a ledge.
    if (toward === dir && Math.abs(player.x - e.x) > 1.2) {
      walk(e, def, grid, def.speed, dt, crumble);
      // walk() may have turned it at a ledge; keep facing the player.
      e.facing = dir === 1 ? 'right' : 'left';
    } else {
      e.vx = 0;
    }
  } else {
    e.state = 'move';
    walk(e, def, grid, def.speed * 0.6, dt, crumble);
  }
}

function stepHop(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  player: EnemyPlayerView,
  dt: number,
  events: PlatformerEvent[],
  crumble?: Record<number, CrumbleTileState>,
): void {
  // Airborne: keep the hop's horizontal speed, land, then recover.
  const wasGrounded = e.isGrounded;
  applyWalkerGravity(e, def, grid, dt, crumble);
  if (e.state === 'attack') {
    const m = moveAxis(grid, e, def, e.vx * dt, 0, crumble);
    e.x = m.x;
    if (m.blockedX) e.vx = 0;
    if (e.isGrounded && !wasGrounded) {
      e.state = 'recover';
      e.stateTimer = 0.25;
      e.vx = 0;
    }
    return;
  }
  e.vx = 0;
  if (e.state === 'recover') {
    e.stateTimer -= dt;
    if (e.stateTimer <= 0) {
      e.state = 'idle';
      e.cooldownTimer = def.attackCooldownSeconds;
    }
    return;
  }
  if (e.state === 'telegraph') {
    e.stateTimer -= dt;
    e.facing = faceToward(e, player.x);
    if (e.stateTimer <= 0 && e.isGrounded) {
      e.state = 'attack';
      e.vy = -(def.hopImpulse ?? 9);
      e.vx = (e.facing === 'right' ? 1 : -1) * def.speed;
      e.isGrounded = false;
      events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
    }
    return;
  }
  e.state = 'idle';
  if (e.isGrounded && e.cooldownTimer <= 0 && within(e, player, def.aggroRange) && !player.isPhasing) {
    e.state = 'telegraph';
    e.stateTimer = def.telegraphSeconds;
    e.facing = faceToward(e, player.x);
    events.push({ type: 'enemy-telegraph', enemyId: e.id, x: e.x, y: e.y });
  }
}

function stepFlyer(e: EnemyRuntime, def: EnemyDef, player: EnemyPlayerView, dt: number, events: PlatformerEvent[]): void {
  const bob = (def.bobAmplitude ?? 0.3) * Math.sin(e.clock * 3);
  // `phase` stores the patrol height for flyers (a boss-only field otherwise).
  if (e.state === 'idle') {
    e.state = 'move';
    e.phase = e.y;
  }
  const homeY = e.phase;
  if (e.state === 'move') {
    const dir = e.facing === 'right' ? 1 : -1;
    e.vx = dir * def.speed;
    e.x += e.vx * dt;
    if (def.minX !== undefined && e.x < def.minX) {
      e.x = def.minX;
      e.facing = 'right';
    }
    if (def.maxX !== undefined && e.x > def.maxX) {
      e.x = def.maxX;
      e.facing = 'left';
    }
    e.y = homeY + bob;
    if (e.cooldownTimer <= 0 && within(e, player, def.aggroRange) && !player.isPhasing) {
      e.state = 'telegraph';
      e.stateTimer = def.telegraphSeconds;
      e.facing = faceToward(e, player.x);
      events.push({ type: 'enemy-telegraph', enemyId: e.id, x: e.x, y: e.y });
    }
    return;
  }
  if (e.state === 'telegraph') {
    e.stateTimer -= dt;
    e.vx = 0;
    e.y = homeY + bob;
    if (e.stateTimer <= 0) {
      // Dive at the player's position as of now.
      const dx = player.x - e.x;
      const dy = player.y - e.y;
      const len = Math.hypot(dx, dy) || 1;
      const diveSpeed = def.speed * 2.2;
      e.vx = (dx / len) * diveSpeed;
      e.vy = (dy / len) * diveSpeed;
      e.state = 'attack';
      e.stateTimer = FLYER_DIVE_SECONDS;
      e.facing = dx < 0 ? 'left' : 'right';
      events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
    }
    return;
  }
  if (e.state === 'attack') {
    e.stateTimer -= dt;
    e.x += e.vx * dt;
    e.y += e.vy * dt;
    if (e.stateTimer <= 0) {
      e.state = 'recover';
      e.stateTimer = FLYER_RETURN_SECONDS;
      e.cooldownTimer = def.attackCooldownSeconds;
    }
    return;
  }
  if (e.state === 'recover') {
    e.stateTimer -= dt;
    const k = Math.min(1, dt * 4);
    e.y += (homeY + bob - e.y) * k;
    e.vx = 0;
    e.vy = 0;
    if (e.stateTimer <= 0) {
      e.state = 'move';
      e.y = homeY + bob;
    }
  }
}

function stepTurret(
  e: EnemyRuntime,
  def: EnemyDef,
  player: EnemyPlayerView,
  dt: number,
  events: PlatformerEvent[],
  projectiles: Projectile[],
): void {
  e.vx = 0;
  e.vy = 0;
  if (e.state === 'telegraph') {
    e.stateTimer -= dt;
    if (e.stateTimer <= 0) {
      e.state = 'attack';
      e.stateTimer = 0.2;
      if (def.projectile) {
        const dx = player.x - e.x;
        const dy = player.y - e.y;
        projectiles.push(makeProjectile('enemy', def.projectile, e.x, e.y, dx, dy));
        events.push({ type: 'projectile', owner: 'enemy', kind: def.projectile.kind, x: e.x, y: e.y });
      }
      events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
      e.cooldownTimer = def.attackCooldownSeconds;
    }
    return;
  }
  if (e.state === 'attack') {
    e.stateTimer -= dt;
    if (e.stateTimer <= 0) e.state = 'idle';
    return;
  }
  e.state = 'idle';
  if (within(e, player, def.aggroRange) && !player.isPhasing) {
    e.facing = faceToward(e, player.x);
    if (e.cooldownTimer <= 0) {
      e.state = 'telegraph';
      e.stateTimer = def.telegraphSeconds;
      events.push({ type: 'enemy-telegraph', enemyId: e.id, x: e.x, y: e.y });
    }
  }
}

function stepCharger(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  player: EnemyPlayerView,
  dt: number,
  events: PlatformerEvent[],
  crumble?: Record<number, CrumbleTileState>,
): void {
  applyWalkerGravity(e, def, grid, dt, crumble);
  if (e.state === 'attack') {
    const turned = walk(e, def, grid, def.speed, dt, crumble);
    if (turned) {
      // Hit a wall or a ledge: stop and recover, facing where it was going.
      e.facing = e.facing === 'right' ? 'left' : 'right';
      e.state = 'recover';
      e.stateTimer = CHARGE_RECOVER_SECONDS;
      e.vx = 0;
    }
    return;
  }
  e.vx = 0;
  if (e.state === 'recover') {
    e.stateTimer -= dt;
    if (e.stateTimer <= 0) {
      e.state = 'idle';
      e.cooldownTimer = def.attackCooldownSeconds;
    }
    return;
  }
  if (e.state === 'telegraph') {
    e.stateTimer -= dt;
    if (e.stateTimer <= 0) {
      e.state = 'attack';
      events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
    }
    return;
  }
  e.state = 'idle';
  const sameHeight = Math.abs(player.y - e.y) <= 2;
  if (e.isGrounded && e.cooldownTimer <= 0 && sameHeight && within(e, player, def.aggroRange) && !player.isPhasing) {
    e.state = 'telegraph';
    e.stateTimer = def.telegraphSeconds;
    e.facing = faceToward(e, player.x);
    events.push({ type: 'enemy-telegraph', enemyId: e.id, x: e.x, y: e.y });
  }
}

function stepBoss(
  e: EnemyRuntime,
  def: EnemyDef,
  grid: PlatformerGrid,
  player: EnemyPlayerView,
  dt: number,
  events: PlatformerEvent[],
  projectiles: Projectile[],
  crumble?: Record<number, CrumbleTileState>,
): void {
  const spec = def.boss ?? { phases: ['rest'], phaseSeconds: 2, volleyCount: 3 };
  const phaseName = spec.phases[e.phase % spec.phases.length] ?? 'rest';
  const wasGrounded = e.isGrounded;
  applyWalkerGravity(e, def, grid, dt, crumble);

  // Sleeping until the player is in range keeps a boss arena calm on entry.
  if (e.state === 'idle' && e.stateTimer === 0) {
    if (!within(e, player, def.aggroRange) || player.isPhasing) {
      e.vx = 0;
      return;
    }
    e.state = 'telegraph';
    e.stateTimer = def.telegraphSeconds;
    e.facing = faceToward(e, player.x);
    events.push({ type: 'enemy-telegraph', enemyId: e.id, x: e.x, y: e.y });
    return;
  }

  if (e.state === 'telegraph') {
    e.stateTimer -= dt;
    e.vx = 0;
    e.facing = faceToward(e, player.x);
    if (e.stateTimer <= 0) {
      e.state = phaseName === 'rest' ? 'recover' : 'attack';
      e.stateTimer = spec.phaseSeconds;
      if (phaseName === 'slam') {
        e.vy = -14;
        e.isGrounded = false;
      }
      if (phaseName === 'volley' && def.projectile) {
        fireVolley(e, def.projectile, player, spec.volleyCount, projectiles, events);
      }
      if (phaseName !== 'rest') events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
    }
    return;
  }

  if (e.state === 'attack') {
    e.stateTimer -= dt;
    if (phaseName === 'charge') {
      const turned = walk(e, def, grid, def.speed, dt, crumble, false);
      if (turned) e.stateTimer = Math.min(e.stateTimer, 0.3);
    } else if (phaseName === 'slam') {
      // Drift toward the player while airborne, slam on landing.
      if (!e.isGrounded) {
        e.vx = Math.sign(player.x - e.x) * def.speed * 0.6;
        const m = moveAxis(grid, e, def, e.vx * dt, 0, crumble);
        e.x = m.x;
      } else if (!wasGrounded) {
        e.vx = 0;
        events.push({ type: 'enemy-attack', enemyId: e.id, x: e.x, y: e.y });
        if (def.projectile) {
          const ground = { ...def.projectile, gravity: 0 };
          projectiles.push(makeProjectile('enemy', ground, e.x - def.halfWidth, e.y + def.halfHeight - 0.4, -1, 0));
          projectiles.push(makeProjectile('enemy', ground, e.x + def.halfWidth, e.y + def.halfHeight - 0.4, 1, 0));
          events.push({ type: 'projectile', owner: 'enemy', kind: def.projectile.kind, x: e.x, y: e.y });
        }
        e.stateTimer = Math.min(e.stateTimer, 0.4);
      }
    } else {
      e.vx = 0;
    }
    if (e.stateTimer <= 0) nextPhase(e, spec.phases.length);
    return;
  }

  if (e.state === 'recover') {
    // The rest phase: stands still, the intended damage window.
    e.stateTimer -= dt;
    e.vx = 0;
    if (e.stateTimer <= 0) nextPhase(e, spec.phases.length);
  }
}

function nextPhase(e: EnemyRuntime, phaseCount: number): void {
  e.phase = (e.phase + 1) % phaseCount;
  e.state = 'telegraph';
  e.stateTimer = 0.7;
}

function fireVolley(
  e: EnemyRuntime,
  spec: ProjectileSpec,
  player: EnemyPlayerView,
  count: number,
  projectiles: Projectile[],
  events: PlatformerEvent[],
): void {
  const dx = player.x - e.x;
  const dy = player.y - e.y;
  const base = Math.atan2(dy, dx);
  const spread = Math.PI / 5;
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0 : i / (count - 1) - 0.5;
    const a = base + t * spread;
    projectiles.push(makeProjectile('enemy', spec, e.x, e.y, Math.cos(a), Math.sin(a)));
  }
  events.push({ type: 'projectile', owner: 'enemy', kind: spec.kind, x: e.x, y: e.y });
}

/**
 * Apply `damage` to an enemy from the player at `fromX`. Shield enemies
 * facing the source take nothing (but flash briefly so a renderer can
 * clink). Bosses are never stunned. Returns the new runtime and whether
 * damage landed.
 */
export function damageEnemy(
  e: EnemyRuntime,
  def: EnemyDef,
  damage: number,
  fromX: number,
  knockback: number,
  stunSeconds: number,
): { enemy: EnemyRuntime; landed: boolean } {
  if (e.state === 'dead') return { enemy: e, landed: false };
  if (def.behaviour === 'shield') {
    const facingSource = (e.facing === 'right' && fromX >= e.x) || (e.facing === 'left' && fromX <= e.x);
    if (facingSource) return { enemy: { ...e, hurtFlash: 0.05 }, landed: false };
  }
  const dir = fromX < e.x ? 1 : -1;
  const next: EnemyRuntime = {
    ...e,
    hp: e.hp - damage,
    hurtFlash: ENEMY_HURT_FLASH_SECONDS,
  };
  if (def.behaviour !== 'boss') {
    next.vx = dir * knockback;
    if (!def.flying) next.vy = Math.min(next.vy, -knockback * 0.35);
    next.state = stunSeconds > 0 ? 'stunned' : 'hurt';
    next.stateTimer = stunSeconds > 0 ? stunSeconds : 0.15;
  }
  return { enemy: next, landed: true };
}
